import { useEffect, useState } from 'react';

/**
 * useIsPhone
 * ----------
 * Détecte si on est sur un "vrai" téléphone (et non une petite tablette).
 *
 * Règles appliquées (OR/AND combinés) :
 *   1. `userAgent` contient explicitement "iPad" ou "Tablet" → considérée tablette
 *      (même si iPadOS 13+ se fait passer pour "Macintosh", on teste aussi
 *      `navigator.maxTouchPoints > 1` + plateforme "MacIntel" pour détecter
 *      un iPad qui se fait passer pour un Mac).
 *   2. Sinon, on considère "phone" si la largeur < `breakpoint` (défaut 600 px).
 *      → Les petites tablettes 7-8″ (600-768 px) restent en mode desktop/tablette.
 */
function isIpadFakingMac() {
    if (typeof navigator === 'undefined') return false;
    const platform = navigator.platform || '';
    const maxTouch = navigator.maxTouchPoints || 0;
    return platform === 'MacIntel' && maxTouch > 1;
}

function isTabletUA() {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    // iPad ou tout UA qui s'annonce explicitement comme tablette
    if (/\biPad\b/i.test(ua)) return true;
    if (/\bTablet\b/i.test(ua)) return true;
    // Android sans "Mobile" dans l'UA = tablette Android
    if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return true;
    // iPad qui se fait passer pour Mac (Safari iPadOS 13+)
    if (isIpadFakingMac()) return true;
    return false;
}

export function useIsPhone(breakpoint = 600) {
    const get = () => {
        if (typeof window === 'undefined') return false;
        if (isTabletUA()) return false; // Tablettes : jamais en mode "phone"
        return window.innerWidth < breakpoint;
    };
    const [isPhone, setIsPhone] = useState(get);
    useEffect(() => {
        const onResize = () => setIsPhone(get());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return isPhone;
}
